import React, { useEffect, useRef, useState } from "react";
import { TIER_META } from "../../data/casesData";
import { playTick } from "../../lib/sounds";
import ItemImage from "./ItemImage";

const TILE_DESKTOP = 188;
const TILE_MOBILE = 118;
const COUNT = 64;
const WIN = 56;
const getTile = () => (typeof window !== "undefined" && window.innerWidth < 640 ? TILE_MOBILE : TILE_DESKTOP);

function buildStrip(caseObj, winner) {
  const items = caseObj.items;
  const rarest = [...items].sort((a, b) => a.rate - b.rate).slice(0, 4); // top rares/mythics
  // Base: every item equally likely (so rares show as often as commons).
  const arr = Array.from({ length: COUNT }, () => items[Math.floor(Math.random() * items.length)]);
  // Sprinkle in extra grails so mythics keep flashing by as bait.
  for (let i = 0; i < COUNT; i++) if (Math.random() < 0.18) arr[i] = rarest[Math.floor(Math.random() * rarest.length)];
  if (winner) {
    arr[WIN] = winner;
    // Flank the winner with grails for a tense near-miss.
    arr[WIN - 1] = rarest[Math.floor(Math.random() * rarest.length)];
    arr[WIN + 1] = rarest[Math.floor(Math.random() * rarest.length)];
  }
  return arr;
}

export default function OpeningReel({ caseObj, winner, spinKey, onDone, fast = false }) {
  const [strip, setStrip] = useState(() => buildStrip(caseObj, null));
  const [tx, setTx] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [cfg, setCfg] = useState({ dur: 5.5, ease: "cubic-bezier(0.12,0.82,0.24,1.02)" });
  const [tile, setTile] = useState(getTile);
  const wrapRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    const onResize = () => setTile(getTile());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!spinKey) return;
    const w = wrapRef.current ? wrapRef.current.clientWidth : 600;
    const s = buildStrip(caseObj, winner);
    setStrip(s);
    setSpinning(false);
    setTx(0);
    let rafId, tickRaf;
    // Randomize: land anywhere within the winning tile (center → near edge) so
    // it can "edge" close to the neighbor, and vary the overshoot so it baits
    // past toward the next item before settling.
    const edge = (Math.random() * 2 - 1) * (tile * 0.40); // stays on the winner
    const dur = fast ? 0.8 + Math.random() * 0.3           // fast: ~0.8 – 1.1s
                     : 8.2 + Math.random() * 1.8;          // 8.2 – 10.0s
    const settleDur = fast ? 0.28 : 0.75;
    const doneGap = fast ? 360 : 860;
    const eases = [
      "cubic-bezier(0.12,0.82,0.24,1.005)",
      "cubic-bezier(0.12,0.82,0.24,1.015)",
      "cubic-bezier(0.11,0.83,0.23,1.02)",
      "cubic-bezier(0.13,0.81,0.25,1.025)",
      "cubic-bezier(0.12,0.82,0.24,1.00)",
    ];
    const ease = eases[Math.floor(Math.random() * eases.length)];
    const base = -(WIN * tile + tile / 2 - w / 2);   // exact center of winner
    const target = base + edge;                       // where it first lands (edged)
    let settleTimer, doneTimer;
    rafId = requestAnimationFrame(() => requestAnimationFrame(() => {
      setCfg({ dur, ease });
      setSpinning(true);
      setTx(target);
      // Tick loop: read the LIVE transform each frame and tick on every real
      // tile crossing -> sound naturally slows down with the reel.
      let nextThreshold = tile * 0.6;
      const readX = () => {
        const el = trackRef.current;
        if (!el) return 0;
        const t = getComputedStyle(el).transform;
        if (!t || t === "none") return 0;
        const m = t.match(/matrix.*\((.+)\)/);
        if (!m) return 0;
        const p = m[1].split(", ");
        return Math.abs(parseFloat(p[4] || p[12] || 0));
      };
      const loop = () => {
        const x = readX();
        while (x >= nextThreshold) { playTick(); nextThreshold += tile; }
        tickRaf = requestAnimationFrame(loop);
      };
      tickRaf = requestAnimationFrame(loop);

      // After it lands, gently pull to the exact center of the item, then show result.
      settleTimer = setTimeout(() => {
        setCfg({ dur: settleDur, ease: "cubic-bezier(0.22,1,0.36,1)" });
        setTx(base);
      }, dur * 1000 + 60);
      doneTimer = setTimeout(() => onDone && onDone(), dur * 1000 + 60 + settleDur * 1000 + doneGap);
    }));
    return () => { cancelAnimationFrame(rafId); cancelAnimationFrame(tickRaf); clearTimeout(settleTimer); clearTimeout(doneTimer); };
    // eslint-disable-next-line
  }, [spinKey]);

  return (
    <div ref={wrapRef} className="relative overflow-hidden">
      {/* center marker */}
      <div className="absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-[3px] z-20"
        style={{ background: caseObj.accent, boxShadow: `0 0 16px 2px ${caseObj.accent}` }} />
      <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-20 text-lg" style={{ color: caseObj.accent }}>▼</div>
      <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 z-20 text-lg" style={{ color: caseObj.accent }}>▲</div>
      {/* edge fades */}
      <div className="absolute inset-y-0 left-0 w-16 sm:w-40 z-10 pointer-events-none" style={{ background: "linear-gradient(90deg, #090C14 0%, transparent 100%)" }} />
      <div className="absolute inset-y-0 right-0 w-16 sm:w-40 z-10 pointer-events-none" style={{ background: "linear-gradient(270deg, #090C14 0%, transparent 100%)" }} />

      <div ref={trackRef} className="flex py-4 sm:py-6"
        style={{ transform: `translateX(${tx}px)`, transition: spinning ? `transform ${cfg.dur}s ${cfg.ease}` : "none", willChange: "transform" }}>
        {strip.map((it, i) => {
          const color = TIER_META[it.tier].color;
          return (
            <div key={i} style={{ width: tile }} className="shrink-0 px-1.5 sm:px-2 flex flex-col items-center justify-center gap-2 sm:gap-3">
              <div className="relative h-24 w-24 sm:h-40 sm:w-40 flex items-center justify-center">
                <span className="absolute inset-2 sm:inset-3 rounded-full blur-xl sm:blur-2xl opacity-70" style={{ background: `radial-gradient(circle, ${color} 0%, transparent 70%)` }} />
                <div className="relative" style={{ filter: `drop-shadow(0 6px 14px ${color}cc)` }}>
                  <ItemImage item={it} imgClass="h-[76px] w-[76px] sm:h-32 sm:w-32" iconClass="h-12 w-12 sm:h-14 sm:w-14" color={color} eager />
                </div>
              </div>
              <span className="text-[11px] sm:text-[13px] font-semibold text-white/90 text-center leading-tight line-clamp-1 max-w-[110px] sm:max-w-[170px]">{it.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
